import { useEffect, useMemo, useRef } from 'react';
import uPlot from 'uplot';
import 'uplot/dist/uPlot.min.css';

// ---------------------------------------------------------------------------
// UPlotChart — the ONLY uPlot touchpoint in the app.
//
// * Instance held in a ref; setData on data change; full re-create when the
//   series CONFIG changes (labels/colors/scales); destroy on unmount.
//   React 18 StrictMode-safe: the create effect destroys any existing
//   instance first and its cleanup nulls the ref, so mount→unmount→mount
//   never leaks a chart.
// * Drag-select on x calls onZoom(from, to); double-click calls
//   onZoom(null, null) (reset). Zooming is server-side: the parent refetches
//   and pushes new data + timeRange — uPlot never rescales itself.
// * Markers (event vlines + glyphs) and bands (test-run windows) are drawn
//   via hooks.draw on the canvas, reading refs so no re-create is needed.
// * Hover legend with per-series values is uPlot's built-in legend.
// ---------------------------------------------------------------------------

/** Re-export so page code never has to import uplot directly. */
export type AlignedChartData = uPlot.AlignedData;

export interface ChartSeriesConfig {
  label: string;
  color: string;
  /** 'y' (left, default) or 'y2' (right — RSSI / channel-util overlays). */
  scale?: 'y' | 'y2';
  dash?: number[];
  width?: number;
}

export type MarkerShape =
  | 'triangle'
  | 'triangleDown'
  | 'circle'
  | 'circleOpen'
  | 'square'
  | 'cross'
  | 'diamond'
  | 'diamondOpen'
  | 'dot';

export interface ChartMarker {
  ts: number;
  color: string;
  shape: MarkerShape;
}

export interface ChartBand {
  from: number;
  to: number;
  color: string; // rgba(...) recommended — filled behind the plot
}

interface UPlotChartProps {
  series: ChartSeriesConfig[];
  /** Aligned arrays: [xs, ...ys] — xs are epoch seconds. */
  data: uPlot.AlignedData;
  /** X-scale window (epoch seconds) — keeps chart, strips and markers on one axis. */
  timeRange: [number, number];
  yLabel: string;
  y2Label?: string;
  markers?: ChartMarker[];
  bands?: ChartBand[];
  /** Cursor-sync group key (both panes share one). */
  syncKey?: string;
  onZoom: (from: number | null, to: number | null) => void;
  height?: number;
}

function drawGlyph(
  ctx: CanvasRenderingContext2D,
  shape: MarkerShape,
  x: number,
  y: number,
  r: number,
  color: string
) {
  ctx.beginPath();
  ctx.fillStyle = color;
  ctx.strokeStyle = color;
  ctx.lineWidth = Math.max(1, r / 3);
  switch (shape) {
    case 'triangle':
      ctx.moveTo(x, y - r);
      ctx.lineTo(x + r, y + r);
      ctx.lineTo(x - r, y + r);
      ctx.closePath();
      ctx.fill();
      break;
    case 'triangleDown':
      ctx.moveTo(x, y + r);
      ctx.lineTo(x + r, y - r);
      ctx.lineTo(x - r, y - r);
      ctx.closePath();
      ctx.fill();
      break;
    case 'circle':
      ctx.arc(x, y, r, 0, Math.PI * 2);
      ctx.fill();
      break;
    case 'circleOpen':
      ctx.arc(x, y, r, 0, Math.PI * 2);
      ctx.stroke();
      break;
    case 'square':
      ctx.fillRect(x - r, y - r, r * 2, r * 2);
      break;
    case 'cross':
      ctx.moveTo(x - r, y - r);
      ctx.lineTo(x + r, y + r);
      ctx.moveTo(x + r, y - r);
      ctx.lineTo(x - r, y + r);
      ctx.stroke();
      break;
    case 'diamond':
      ctx.moveTo(x, y - r);
      ctx.lineTo(x + r, y);
      ctx.lineTo(x, y + r);
      ctx.lineTo(x - r, y);
      ctx.closePath();
      ctx.fill();
      break;
    case 'diamondOpen':
      ctx.moveTo(x, y - r);
      ctx.lineTo(x + r, y);
      ctx.lineTo(x, y + r);
      ctx.lineTo(x - r, y);
      ctx.closePath();
      ctx.stroke();
      break;
    case 'dot':
      ctx.arc(x, y, Math.max(1.5, r / 2), 0, Math.PI * 2);
      ctx.fill();
      break;
  }
}

export default function UPlotChart({
  series,
  data,
  timeRange,
  yLabel,
  y2Label,
  markers = [],
  bands = [],
  syncKey,
  onZoom,
  height = 220,
}: UPlotChartProps) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const plotRef = useRef<uPlot | null>(null);

  // Refs read by uPlot hooks/closures so config/data changes don't force a
  // chart re-create.
  const markersRef = useRef(markers);
  const bandsRef = useRef(bands);
  const timeRangeRef = useRef(timeRange);
  const onZoomRef = useRef(onZoom);
  const dataRef = useRef(data);
  markersRef.current = markers;
  bandsRef.current = bands;
  timeRangeRef.current = timeRange;
  onZoomRef.current = onZoom;
  dataRef.current = data;

  // Series-config identity: full re-create when this changes.
  const seriesKey = useMemo(() => JSON.stringify(series), [series]);
  const hasY2 = series.some((s) => s.scale === 'y2');

  // Create / destroy (config changes only).
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    // StrictMode / config-change guard: never two instances on one container.
    if (plotRef.current) {
      plotRef.current.destroy();
      plotRef.current = null;
    }

    const width = Math.max(el.clientWidth, 240);

    const drawOverlays = (u: uPlot) => {
      const ctx = u.ctx;
      const { left, top, width: bw, height: bh } = u.bbox;
      const dpr = uPlot.pxRatio || window.devicePixelRatio || 1;
      const [xMin, xMax] = timeRangeRef.current;

      ctx.save();
      ctx.beginPath();
      ctx.rect(left, top, bw, bh);
      ctx.clip();

      for (const band of bandsRef.current) {
        if (band.to < xMin || band.from > xMax) continue;
        const x0 = u.valToPos(Math.max(band.from, xMin), 'x', true);
        const x1 = u.valToPos(Math.min(band.to, xMax), 'x', true);
        ctx.fillStyle = band.color;
        ctx.fillRect(x0, top, Math.max(x1 - x0, 1), bh);
      }

      const glyphR = 4 * dpr;
      for (const m of markersRef.current) {
        if (m.ts < xMin || m.ts > xMax) continue;
        const x = u.valToPos(m.ts, 'x', true);
        ctx.globalAlpha = 0.45;
        ctx.strokeStyle = m.color;
        ctx.lineWidth = dpr; // 1 css px
        ctx.beginPath();
        ctx.moveTo(x, top);
        ctx.lineTo(x, top + bh);
        ctx.stroke();
        ctx.globalAlpha = 1;
        drawGlyph(ctx, m.shape, x, top + glyphR + 2 * dpr, glyphR, m.color);
      }

      ctx.restore();
    };

    const handleSelect = (u: uPlot) => {
      if (u.select.width > 5) {
        const from = u.posToVal(u.select.left, 'x');
        const to = u.posToVal(u.select.left + u.select.width, 'x');
        u.setSelect({ left: 0, top: 0, width: 0, height: 0 }, false);
        if (to > from) onZoomRef.current(from, to);
      }
    };

    const axisFont = '11px system-ui, sans-serif';
    const opts: uPlot.Options = {
      width,
      height,
      cursor: {
        drag: { x: true, y: false, setScale: false },
        ...(syncKey ? { sync: { key: syncKey } } : {}),
        points: { size: 6 },
      },
      scales: {
        x: {
          time: true,
          range: (): [number, number] => [timeRangeRef.current[0], timeRangeRef.current[1]],
        },
        y: { auto: true },
        ...(hasY2 ? { y2: { auto: true } } : {}),
      },
      axes: [
        {
          stroke: '#8a97a6',
          font: axisFont,
          grid: { stroke: '#eef1f5', width: 1 },
          ticks: { stroke: '#e4e9f0', width: 1 },
        },
        {
          scale: 'y',
          label: yLabel,
          labelFont: axisFont,
          stroke: '#54626f',
          font: axisFont,
          grid: { stroke: '#eef1f5', width: 1 },
          ticks: { stroke: '#e4e9f0', width: 1 },
          size: 56,
        },
        ...(hasY2
          ? [
              {
                scale: 'y2',
                side: 1,
                label: y2Label ?? '',
                labelFont: axisFont,
                stroke: '#8a97a6',
                font: axisFont,
                grid: { show: false },
                ticks: { stroke: '#e4e9f0', width: 1 },
                size: 52,
              } as uPlot.Axis,
            ]
          : []),
      ],
      series: [
        {},
        ...series.map(
          (s): uPlot.Series => ({
            label: s.label,
            stroke: s.color,
            width: s.width ?? 1.5,
            dash: s.dash,
            scale: s.scale ?? 'y',
            spanGaps: false, // gaps in the sample stream must stay visible
            points: { show: false },
          })
        ),
      ],
      legend: { show: true },
      hooks: {
        draw: [drawOverlays],
        setSelect: [handleSelect],
      },
    };

    const u = new uPlot(opts, dataRef.current, el);
    plotRef.current = u;

    const dblClick = () => onZoomRef.current(null, null);
    u.over.addEventListener('dblclick', dblClick);

    const ro = new ResizeObserver(() => {
      const inst = plotRef.current;
      const node = containerRef.current;
      if (inst && node && node.clientWidth > 0) {
        inst.setSize({ width: node.clientWidth, height });
      }
    });
    ro.observe(el);

    return () => {
      ro.disconnect();
      u.over.removeEventListener('dblclick', dblClick);
      u.destroy();
      if (plotRef.current === u) plotRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [seriesKey, syncKey, height, yLabel, y2Label, hasY2]);

  // Data / range updates (no re-create).
  useEffect(() => {
    const u = plotRef.current;
    if (!u) return;
    u.setData(data, false);
    u.setScale('x', { min: timeRange[0], max: timeRange[1] });
  }, [data, timeRange]);

  // Marker/band-only changes just need a repaint.
  useEffect(() => {
    plotRef.current?.redraw();
  }, [markers, bands]);

  return <div ref={containerRef} style={{ width: '100%' }} />;
}
